import {createSlice, Draft, PayloadAction} from '@reduxjs/toolkit'
import {LanguageOptions} from '../../i18n'
import {ThemeMode} from '../../material.theme'
import localStorageService from '../../services/local.storage'
import {AsyncStateStatus} from "../root.types";

export enum LayoutTypes {
    DATABASE_STATUS = '@layout/changeDatabaseStatus',
    STATES_API_STATUS = '@layout/changeStateStatus'
}

export interface MonitoringStatus {
    readonly status: AsyncStateStatus,
    readonly data: {
        rfb: {
            person: boolean,
            company: boolean
        },
        internal_apis: [{
            person: {
                nfe: boolean,
                nfce: boolean,
                nf3e: boolean,
                nfcom: boolean,
                ipva: boolean,
                cad_sefaz: boolean
            },
            company: {
                nfe: boolean,
                nf3e: boolean,
                nfcom: boolean,
                ipva: boolean,
                cad_sefaz: boolean
            },
            address: {
                nfe: boolean
            },
            contact: {
                nfe: boolean
            },
            fiscal_document: {
                nfe: boolean,
                cte: boolean,
                mdfe: boolean,
                nfe_danfe: boolean
            },
            institution: {
                tce: boolean
            },
            vehicle: {
                ipva: boolean
            }
        }]
    }
}

export interface StateStatus {
    readonly status: AsyncStateStatus,
    readonly data: {
        [key: string]: boolean,
    },
}

export interface LayoutState {
    readonly username: string
    readonly language: LanguageOptions
    readonly breadCrumbLast: string
    readonly themeMode: ThemeMode
    readonly updateAvailable: boolean
    readonly registration: ServiceWorkerRegistration | undefined
    readonly monitoringStatus: MonitoringStatus,
    readonly statesStatus: StateStatus,
}

export interface IActionsChangeLanguage {
    readonly language: LanguageOptions
}

export interface IActionsChangeBreadCrumbLast {
    readonly breadCrumbLast: string | undefined
}

export interface IActionsChangeUserName {
    readonly username: string
}

export interface IActionsChangeThemeMode {
    readonly themeMode: ThemeMode
}

export interface IActionsChangeUpdateAvailable {
    readonly updateAvailable: boolean,
    readonly registration: ServiceWorkerRegistration | undefined
}

const initialState: LayoutState = {
    username: '',
    language: LanguageOptions.PT_BR,
    breadCrumbLast: '',
    themeMode: ThemeMode.LIGHT,
    updateAvailable: false,
    registration: undefined,
    monitoringStatus: {
        status: AsyncStateStatus.INITIAL,
        data: {
            rfb: {
                person: false,
                company: false
            },
            internal_apis: [{
                person: {
                    nfe: false,
                    nfce: false,
                    nf3e: false,
                    nfcom: false,
                    ipva: false,
                    cad_sefaz: false
                },
                company: {
                    nfe: false,
                    nf3e: false,
                    nfcom: false,
                    ipva: false,
                    cad_sefaz: false
                },
                address: {
                    nfe: false
                },
                contact: {
                    nfe: false
                },
                fiscal_document: {
                    nfe: false,
                    cte: false,
                    mdfe: false,
                    nfe_danfe: false
                },
                institution: {
                    tce: false
                },
                vehicle: {
                    ipva: false
                }
            }]
        }
    },
    statesStatus: {
        status: AsyncStateStatus.INITIAL,
        data: { }
    }
}

export const layoutSlice = createSlice({
    name: '@layout',
    initialState,
    reducers: {
        changeLanguage: (state: Draft<LayoutState>, action: PayloadAction<IActionsChangeLanguage>) => {
            const { language } = action.payload
            localStorageService.setItem('language', language)
            state.language = language
        },
        changeBreadCrumbLast: (state: Draft<LayoutState>, action: PayloadAction<IActionsChangeBreadCrumbLast>) => {
            const { breadCrumbLast } = action.payload
            state.breadCrumbLast = `${breadCrumbLast || ''}`
        },
        changeUserName: (state: Draft<LayoutState>, action: PayloadAction<IActionsChangeUserName>) => {
            const { username } = action.payload
            localStorageService.setItem('username', username)
            state.username = username
        },
        changeTheme: (state: Draft<LayoutState>, action: PayloadAction<IActionsChangeThemeMode>) => {
            const { themeMode } = action.payload
            localStorageService.setItem('themeMode', themeMode)
            state.themeMode = themeMode
        },
        changeUpdateAvailable: (state: Draft<LayoutState>, action: PayloadAction<IActionsChangeUpdateAvailable>) => {
            const { updateAvailable, registration } = action.payload
            state.updateAvailable = updateAvailable
            state.registration = registration
        },
        changeDatabaseStatus: (state: Draft<LayoutState>) => {
            state.monitoringStatus.status = AsyncStateStatus.LOADING
        },
        changeDatabaseStatusSuccess: (state: Draft<LayoutState>, action: PayloadAction<any>) => {
            state.monitoringStatus.status = AsyncStateStatus.SUCCESS
            state.monitoringStatus.data = action.payload.monitoringStatus
        },
        changeDatabaseStatusFailure: (state: Draft<LayoutState>) => {
            state.monitoringStatus.status = AsyncStateStatus.FAILURE
            state.monitoringStatus = initialState.monitoringStatus
        },
        changeStateStatus: (state: Draft<LayoutState>) => {
            state.statesStatus.status = AsyncStateStatus.LOADING
        },
        changeStateStatusSuccess: (state: Draft<LayoutState>, action: PayloadAction<any>) => {
            state.statesStatus.status = AsyncStateStatus.SUCCESS
            state.statesStatus.data = action.payload.stateStatus
        },
        changeStateStatusFailure: (state: Draft<LayoutState>) => {
            state.statesStatus.status = AsyncStateStatus.FAILURE
            state.statesStatus = initialState.statesStatus
        },
    }
})

/**
 * <h5>Actions used to manipulate the application's layout.</h5>
 * @see {@link https://redux.js.org/tutorials/fundamentals/part-3-state-actions-reducers#designing-actions}
 * @typedef LayoutActions
 * @namespace LayoutActions
 * @category React
 * @subcategory Redux / Actions
 *
 * @property {Function} changeLanguage
 * @property {Function} changeBreadCrumbLast
 * @property {Function} changeUserName
 * @property {Function} changeTheme
 * @property {Function} changeUpdateAvailable
 */
export const LayoutActions = layoutSlice.actions

export default layoutSlice.reducer
